import { DSExamples } from "@spscommerce/ds-shared";
import { code } from "@spscommerce/utils";

export const SpsModalExamples: DSExamples = {
    types: {
        label: "Styles",
        examples: {
            basic: {
                template: code`
                    <sps-modal id="basic-modal" title="Default Modal">
                        Modal content
                    </sps-modal>
                    <sps-button id="basic-modal-btn" spsModalOpen="basic-modal">
                        Open
                    </sps-button>
                `
            },
            success: {
                template: code`
                    <sps-modal id="success-modal" title="Success Modal" type="success">
                            Modal content
                    </sps-modal>
                    <sps-button spsModalOpen="success-modal">
                            Open
                    </sps-button>
                `
            },
            delete: {
                template: code`
                    <sps-modal id="delete-modal" title="Delete Modal" type="delete">
                        Modal content
                    </sps-modal>
                    <sps-button spsModalOpen="delete-modal">
                        Open
                    </sps-button>
                `
            },
            info: {
                template: code`
                    <sps-modal id="info-modal" title="Info Modal" type="info">
                        Modal content
                    </sps-modal>
                    <sps-button spsModalOpen="info-modal">
                        Open
                    </sps-button>
                `
            },
            tip: {
                template: code`
                    <sps-modal id="tip-modal" title="Tip Modal" type="tip">
                        Modal content
                    </sps-modal>
                    <sps-button spsModalOpen="tip-modal">
                        Open
                    </sps-button>
                `
            },
            warning: {
                template: code`
                    <sps-modal id="warning-modal" title="Warning Modal" type="warning">
                        Modal content
                    </sps-modal>
                    <sps-button spsModalOpen="warning-modal">
                        Open
                    </sps-button>
                `
            }
        }
    },
    sizes: {
        label: "Sizes",
        examples: {
            small: {
                template: code`
                    <sps-modal id="small-modal" title="Small Modal" size="small">
                        Modal content
                    </sps-modal>
                    <sps-button spsModalOpen="small-modal">
                        Open
                    </sps-button>
                `
            },
            large: {
                template: code`
                    <sps-modal id="large-modal" title="Large Modal" size="large">
                        Modal content
                    </sps-modal>
                    <sps-button spsModalOpen="large-modal">
                        Open
                    </sps-button>
                `
            }
        }
    },
    events: {
        label: "Events",
        examples: {
            basic: {
                template: code`
                    <sps-modal id="events-modal" title="Modal Events" (open)="onModalOpened()" (close)="onModalClosed($event)">
                        Modal content
                    </sps-modal>
                    <sps-button spsModalOpen="events-modal">
                        Open
                    </sps-button>
                    <b>Event log:</b>
                    <div [innerHTML]="eventLog"></div>
                `,
                class: code `
                    class MyComponent {
                        eventLog = "";
                        labelLog = "";
                        onModalOpened() {
                            this.eventLog += "Modal opened.<br/>";
                        }
                        onModalClosed(event: ModalEvent) {
                            this.labelLog = (event.action && event.action.label) ? event.action.label : 'undefined';
                            this.eventLog += "Modal closed. <pre class='mb-0'>typeof event.action:"+ typeof event.action +"</pre>";
                            this.eventLog += "<pre class='mb-0'>event.action?.label:"+this.labelLog+"</pre>";
                        }
                    }
                `
            }
        }
    },
    actions: {
        label: "Actions",
        examples: {
            basic: {
                description: code`
                    <p>The buttons in a modal are determined by its actions input. An action can be a
                    string - in which case the button will be labeled with that string and do nothing
                    special except close the modal when clicked. Otherwise, it can be a method decorated
                    with <code>@ModalAction()</code>. In this case that method will be run when the
                    button is clicked.</p>
                `,
                template: code`
                    <sps-modal id="actions-modal" title="Modal Actions" [actions]="['Cancel', incrementN]">
                        Modal content
                    </sps-modal>
                    <sps-button spsModalOpen="actions-modal">
                        Open ({{n}})
                    </sps-button>
                `,
                class: code`
                    class MyComponent {
                        n = 0;

                        @ModalAction({ label: "Increment" })
                        incrementN() {
                            this.n++;
                        }
                    }
                `
            },
            actionRequired: {
                description: code`
                    <p>If you set <code>actionRequired</code> on the modal, then it cannot be dismissed with
                    ESC or an "x" button in the title bar. The user must select one of the action buttons.</p>
                `,
                template: code`
                    <sps-modal id="action-required-modal" title="Modal Actions"
                        [actions]="['Cancel', 'OK']" actionRequired>
                        Since this modal has "actionRequired", you cannot close it by clicking outside of it
                        or pressing ESC. You <b>must</b> choose one of the action buttons.
                    </sps-modal>
                    <sps-button id="action-required-modal-btn" spsModalOpen="action-required-modal">
                        Open
                    </sps-button>
                `
            },
            stayOpen: {
                template: code`
                    <sps-modal id="stay-open-modal" title="Modal Actions" [actions]="['Close', stayOpen]">
                        The stayOpen method returns false, so the modal won't close when it runs.
                        Normally this will be conditional. If the action is async (see below) then you can
                        achieve this by the Observable or Promise emitting/resolving with the value false.
                    </sps-modal>
                    <sps-button id="stay-open-modal-btn" spsModalOpen="stay-open-modal">
                        Open
                    </sps-button>
                `,
                class: code`
                class MyComponent {
                    @ModalAction({ label: 'Stay Open' })
                    stayOpen() {
                        return false;
                    }
                }`
            },
            asyncActionModal: {
                template: code`
                    <sps-modal id="async-action-modal" title="Modal Actions" [actions]="['Cancel', thinkForAFew]">
                        If an action returns a Promise or Observable, the modal will wait for it to complete.
                    </sps-modal>
                    <sps-button id="async-action-modal-btn" spsModalOpen="async-action-modal">
                        Open
                    </sps-button>
                `,
                class: code`class MyComponent {
                    @ModalAction({ label: 'Think for a few' })
                    thinkForAFew() {
                        return new Promise(resolve => {
                            window.setTimeout(resolve, 3000);
                        });
                    }
                }`
            },
            disabledAction: {
                template: code`
                    <sps-modal id="disabled-action-modal" title="Modal Actions" [actions]="[yep, nope]">
                        You can control whether an action is enabled or disabled.
                        In fact, any property of the config can be a function that takes in the
                        component class instance and returns the appropriate value.
                    </sps-modal>
                    <sps-button id="disabled-action-modal-btn" spsModalOpen="disabled-action-modal">
                        Open
                    </sps-button>
                `,
                class: code`class MyComponent {
                overrideNope = false;
                @ModalAction({
                    label(componentInstance: MyComponent) {
                        return componentInstance.overrideNope ? "I GUESS SO" : "Nope";
                    },
                    enabled(componentInstance: MyComponent) {
                        return componentInstance.overrideNope;
                    }
                })
                nope() {
                    // nope
                }
                @ModalAction({ label: "Yep" })
                yep() {
                    this.overrideNope = true;
                    return false; // Keep the modal open
                }
                }`
            }
        }
    }
};
